// src/components/admin/compensation/AssignEmployeesModal.js
// Modal for bulk assigning employees to a pay group

import React, { useState, useEffect } from 'react';
import { X, Search, Users, UserCheck, DollarSign, CheckSquare, Square } from 'lucide-react';
import { useTheme } from '../../../contexts/ThemeContext';
import payGroupsService from '../../../services/payGroups.service';
import employeeService from '../../../services/employee.service';

const AssignEmployeesModal = ({ isOpen, onClose, onAssign, payGroup }) => {
    const { theme } = useTheme();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [searchQuery, setSearchQuery] = useState('');
    const [activeTab, setActiveTab] = useState('unassigned'); // 'unassigned' or 'assigned'
    const [unassignedEmployees, setUnassignedEmployees] = useState([]);
    const [assignedEmployees, setAssignedEmployees] = useState([]);
    const [selectedEmployees, setSelectedEmployees] = useState([]);
    const [applySalaryStructure, setApplySalaryStructure] = useState(true);

    useEffect(() => {
        if (isOpen && payGroup) {
            fetchEmployees();
        }
    }, [isOpen, payGroup]);

    const fetchEmployees = async () => {
        try {
            setLoading(true);

            // Fetch unassigned employees
            const unassignedResponse = await payGroupsService.getUnassignedEmployees();
            if (unassignedResponse.success) {
                setUnassignedEmployees(unassignedResponse.employees || []);
            }

            // Fetch currently assigned employees in this pay group
            const assignedResponse = await payGroupsService.getPayGroupEmployees(payGroup.id);
            if (assignedResponse.success) {
                setAssignedEmployees(assignedResponse.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
            alert('Failed to load employees');
        } finally {
            setLoading(false);
        }
    };

    const handleSelectEmployee = (employeeId) => {
        setSelectedEmployees(prev => {
            if (prev.includes(employeeId)) {
                return prev.filter(id => id !== employeeId);
            } else {
                return [...prev, employeeId];
            }
        });
    };

    const handleSelectAll = (employees) => {
        const employeeIds = employees.map(e => e.employee_id);
        if (selectedEmployees.length === employeeIds.length) {
            // Deselect all
            setSelectedEmployees([]);
        } else {
            // Select all visible employees
            setSelectedEmployees(employeeIds);
        }
    };

    const handleAssign = async () => {
        if (selectedEmployees.length === 0) {
            alert('Please select at least one employee');
            return;
        }

        try {
            setSaving(true);

            const response = await payGroupsService.assignEmployees(
                payGroup.id,
                selectedEmployees,
                applySalaryStructure
            );

            if (response.success) {
                alert(`Successfully assigned ${response.count} employee(s) to ${payGroup.name}`);
                onAssign();
            }
        } catch (error) {
            console.error('Error assigning employees:', error);
            alert(error.response?.data?.error || 'Failed to assign employees');
        } finally {
            setSaving(false);
        }
    };

    const handleUnassign = async (employeeId) => {
        if (!window.confirm('Are you sure you want to remove this employee from the pay group?')) {
            return;
        }

        try {
            const response = await payGroupsService.unassignEmployee(payGroup.id, employeeId);
            if (response.success) {
                // Refresh employee lists
                fetchEmployees();
                alert('Employee unassigned successfully');
            }
        } catch (error) {
            console.error('Error unassigning employee:', error);
            alert('Failed to unassign employee');
        }
    };

    const formatCurrency = (amount) => {
        if (!amount) return 'Not set';
        return new Intl.NumberFormat('en-IN', {
            style: 'currency',
            currency: 'INR',
            maximumFractionDigits: 0
        }).format(amount);
    };

    const filteredUnassignedEmployees = unassignedEmployees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.employee_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.designation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.department_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    const filteredAssignedEmployees = assignedEmployees.filter(emp =>
        `${emp.first_name} ${emp.last_name}`.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.employee_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.designation?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        emp.department_name?.toLowerCase().includes(searchQuery.toLowerCase())
    );

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white dark:bg-gray-800 rounded-lg max-w-5xl w-full max-h-[90vh] overflow-hidden">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
                    <div>
                        <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                            Assign Employees to Pay Group
                        </h2>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                            {payGroup.name} • {formatCurrency(payGroup.min_ctc)} - {formatCurrency(payGroup.max_ctc)}
                        </p>
                    </div>
                    <button
                        onClick={onClose}
                        className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                    >
                        <X className="w-5 h-5" />
                    </button>
                </div>

                {/* Tabs */}
                <div className="flex border-b border-gray-200 dark:border-gray-700 px-6">
                    <button
                        onClick={() => setActiveTab('unassigned')}
                        className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                            activeTab === 'unassigned'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <Users className="w-4 h-4" />
                            Unassigned Employees ({unassignedEmployees.length})
                        </div>
                    </button>
                    <button
                        onClick={() => setActiveTab('assigned')}
                        className={`px-4 py-3 font-medium border-b-2 transition-colors ${
                            activeTab === 'assigned'
                                ? 'border-blue-600 text-blue-600 dark:text-blue-400'
                                : 'border-transparent text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                        }`}
                    >
                        <div className="flex items-center gap-2">
                            <UserCheck className="w-4 h-4" />
                            Currently Assigned ({assignedEmployees.length})
                        </div>
                    </button>
                </div>

                {/* Content */}
                <div className="p-6 space-y-4 overflow-y-auto max-h-[calc(90vh-260px)]">
                    {/* Search */}
                    <div className="relative">
                        <Search className="w-5 h-5 absolute left-3 top-2.5 text-gray-400" />
                        <input
                            type="text"
                            placeholder="Search employees by name, code, designation, or department..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
                                focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:text-white"
                        />
                    </div>

                    {loading ? (
                        <div className="text-center py-12 text-gray-500">
                            Loading employees...
                        </div>
                    ) : activeTab === 'unassigned' ? (
                        <>
                            {/* Apply Salary Structure Option */}
                            {selectedEmployees.length > 0 && (
                                <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-4">
                                    <label className="flex items-center gap-3">
                                        <input
                                            type="checkbox"
                                            checked={applySalaryStructure}
                                            onChange={(e) => setApplySalaryStructure(e.target.checked)}
                                            className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                        />
                                        <div>
                                            <span className="text-sm font-medium text-gray-900 dark:text-white">
                                                Automatically apply pay group salary structure
                                            </span>
                                            <p className="text-xs text-gray-600 dark:text-gray-400 mt-1">
                                                This will update employees' salary components based on the pay group template
                                            </p>
                                        </div>
                                    </label>
                                </div>
                            )}

                            {/* Select All */}
                            {filteredUnassignedEmployees.length > 0 && (
                                <div className="flex items-center justify-between pb-2 border-b border-gray-200 dark:border-gray-700">
                                    <button
                                        onClick={() => handleSelectAll(filteredUnassignedEmployees)}
                                        className="flex items-center gap-2 text-sm text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        {selectedEmployees.length === filteredUnassignedEmployees.length ? (
                                            <CheckSquare className="w-4 h-4" />
                                        ) : (
                                            <Square className="w-4 h-4" />
                                        )}
                                        {selectedEmployees.length > 0
                                            ? `${selectedEmployees.length} selected`
                                            : 'Select all'}
                                    </button>
                                </div>
                            )}

                            {/* Unassigned Employees List */}
                            {filteredUnassignedEmployees.length === 0 ? (
                                <div className="text-center py-12">
                                    <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                    <p className="text-gray-500 dark:text-gray-400">
                                        {searchQuery
                                            ? 'No employees found matching your search'
                                            : 'All employees are already assigned to pay groups'}
                                    </p>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    {filteredUnassignedEmployees.map((employee) => (
                                        <div
                                            key={employee.employee_id}
                                            onClick={() => handleSelectEmployee(employee.employee_id)}
                                            className={`p-4 border rounded-lg cursor-pointer transition-all ${
                                                selectedEmployees.includes(employee.employee_id)
                                                    ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                                    : 'border-gray-200 dark:border-gray-700 hover:border-gray-300 dark:hover:border-gray-600'
                                            }`}
                                        >
                                            <div className="flex items-center gap-3">
                                                <input
                                                    type="checkbox"
                                                    checked={selectedEmployees.includes(employee.employee_id)}
                                                    onChange={() => handleSelectEmployee(employee.employee_id)}
                                                    className="w-5 h-5 text-blue-600 rounded focus:ring-2 focus:ring-blue-500"
                                                />
                                                <div className="flex-1">
                                                    <div className="flex items-center gap-2">
                                                        <h4 className="font-semibold text-gray-900 dark:text-white">
                                                            {employee.first_name} {employee.last_name}
                                                        </h4>
                                                        <span className="text-sm text-gray-500 dark:text-gray-400">
                                                            ({employee.employee_code})
                                                        </span>
                                                    </div>
                                                    <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                                                        <span>{employee.designation || 'No designation'}</span>
                                                        <span>•</span>
                                                        <span>{employee.department_name || 'No department'}</span>
                                                        {employee.current_salary && (
                                                            <>
                                                                <span>•</span>
                                                                <span className="flex items-center gap-1">
                                                                    <DollarSign className="w-3 h-3" />
                                                                    {formatCurrency(employee.current_salary)}
                                                                </span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </>
                    ) : (
                        /* Assigned Employees List */
                        filteredAssignedEmployees.length === 0 ? (
                            <div className="text-center py-12">
                                <UserCheck className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                                <p className="text-gray-500 dark:text-gray-400">
                                    {searchQuery
                                        ? 'No employees found matching your search'
                                        : 'No employees assigned to this pay group yet'}
                                </p>
                            </div>
                        ) : (
                            <div className="space-y-2">
                                {filteredAssignedEmployees.map((employee) => (
                                    <div
                                        key={employee.employee_id}
                                        className="p-4 border border-gray-200 dark:border-gray-700 rounded-lg hover:border-gray-300 dark:hover:border-gray-600 transition-all"
                                    >
                                        <div className="flex items-center justify-between">
                                            <div className="flex-1">
                                                <div className="flex items-center gap-2">
                                                    <h4 className="font-semibold text-gray-900 dark:text-white">
                                                        {employee.first_name} {employee.last_name}
                                                    </h4>
                                                    <span className="text-sm text-gray-500 dark:text-gray-400">
                                                        ({employee.employee_code})
                                                    </span>
                                                </div>
                                                <div className="flex items-center gap-4 mt-1 text-sm text-gray-600 dark:text-gray-400">
                                                    <span>{employee.designation || 'No designation'}</span>
                                                    <span>•</span>
                                                    <span>{employee.department_name || 'No department'}</span>
                                                    {employee.current_salary && (
                                                        <>
                                                            <span>•</span>
                                                            <span className="flex items-center gap-1">
                                                                <DollarSign className="w-3 h-3" />
                                                                {formatCurrency(employee.current_salary)}
                                                            </span>
                                                        </>
                                                    )}
                                                </div>
                                            </div>
                                            <button
                                                onClick={() => handleUnassign(employee.employee_id)}
                                                className="px-3 py-1.5 text-sm text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )
                    )}
                </div>

                {/* Footer */}
                {activeTab === 'unassigned' && (
                    <div className="flex items-center justify-between p-6 border-t border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-900">
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                            {selectedEmployees.length > 0
                                ? `${selectedEmployees.length} employee(s) selected`
                                : 'Select employees to assign'}
                        </div>
                        <div className="flex items-center gap-3">
                            <button
                                onClick={onClose}
                                className="px-6 py-2 border border-gray-300 dark:border-gray-600 rounded-lg hover:bg-white dark:hover:bg-gray-800 transition-colors text-gray-700 dark:text-gray-300"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={handleAssign}
                                disabled={saving || selectedEmployees.length === 0}
                                className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                                {saving
                                    ? 'Assigning...'
                                    : `Assign ${selectedEmployees.length > 0 ? `(${selectedEmployees.length})` : ''}`}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default AssignEmployeesModal;
